import React from "react";
import {
    Box,
    Stack,
    IconButton,
    Typography,
    Divider,
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Avatar
} from "@mui/material";
import { ArrowBack, EventNote, History, WorkspacePremium } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

function ViewBandAssignment() {
    const navigate = useNavigate();
    const location = useLocation();

    // Mock data - in a real app, fetch this using employeeId
    const employeeName = location.state?.employeeName || "Bharath M";
    const currentBand = {
        name: "Top Talent",
        assignedDate: "Jan 15, 2026",
        assignedBy: "Aditi Sharma",
        reviewCycle: "Q1 Annual Review",
        color: "success"
    };

    const bandHistory = [
        { id: 1, band: "Core Performer", cycle: "Annual Review 2025", date: "Jan 10, 2025", manager: "Aditi Sharma", rating: "4.2/5" },
        { id: 2, band: "Core Performer", cycle: "Mid-Year Review 2024", date: "July 15, 2024", manager: "Aditi Sharma", rating: "3.8/5" },
        { id: 3, band: "High Potential", cycle: "Annual Review 2024", date: "Jan 05, 2024", manager: "Rajesh Kumar", rating: "4.5/5" },
    ];

    return (
        <Box sx={{ p: 1 }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', pb: 2, mb: 3 }}>
                <IconButton
                    onClick={() => navigate('/manager/performance-management', { state: { activeTab: 2 } })}
                    sx={{ color: '#64748b', borderRadius: '8px', '&:hover': { bgcolor: 'rgba(100, 116, 139, 0.04)' } }}
                >
                    <ArrowBack sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>Back</Typography>
                </IconButton>

                <Divider orientation="vertical" flexItem sx={{ mx: 3, height: 40, alignSelf: 'center' }} />

                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        Performance Band Assignment
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Banding history and category progression for <b>{employeeName}</b>
                    </Typography>
                </Box>
            </Stack>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <CardContent>
                            <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', py: 2 }}>
                                <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.light', mb: 1 }}>
                                    <WorkspacePremium fontSize="large" />
                                </Avatar>
                                <Box>
                                    <Typography variant="overline" color="text.secondary" fontWeight="bold">Current Active Band</Typography>
                                    <Typography variant="h5" fontWeight="800" color="primary.main">
                                        {currentBand.name}
                                    </Typography>
                                </Box>
                                <Chip label="Active" color="success" size="small" sx={{ fontWeight: 700 }} />
                            </Stack>
                            <Divider sx={{ my: 2 }} />
                            <Stack spacing={1.5}>
                                <DetailItem label="Assigned Date" value={currentBand.assignedDate} icon={<EventNote fontSize="small" />} />
                                <DetailItem label="Review Cycle" value={currentBand.reviewCycle} />
                                <DetailItem label="Assigned By" value={currentBand.assignedBy} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column: Previous Band History */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <History color="action" />
                        <Typography variant="h6" fontWeight="700">Previous Bands & History</Typography>
                    </Box>

                    <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <Table>
                            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Review Cycle</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Assigned Band</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Rating</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bandHistory.map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="600">{row.cycle}</Typography>
                                            <Typography variant="caption" color="text.secondary">By {row.manager}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.band}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontWeight: 600, color: '#64748b' }}
                                            />
                                        </TableCell>
                                        <TableCell>{row.rating}</TableCell>
                                        <TableCell color="text.secondary">{row.date}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
}

// Helper Component for the Card details
const DetailItem = ({ label, value, icon }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
            {label}
        </Typography>
        <Typography variant="body2" fontWeight="600">{value}</Typography>
    </Stack>
);

export default ViewBandAssignment;